import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../../services/api';
import { useStatusStream } from '../../../services/useStatusStream';
import Chip from '../shared/Chip';
import Timestamp from '../shared/Timestamp';
import { formatDuration } from '../../../utils/runDuration';

// Run status -> Figma chip variant (PRD-1361). Anything else stays Standard.
const RUN_STATUS_VARIANT = {
  success: 'success',
  failed: 'error',
  running: 'processing',
  queued: 'processing',
};
const runStatusVariant = (status) => RUN_STATUS_VARIANT[String(status || '').toLowerCase()] || 'standard';

// Spec §6.3 — run history surface for push_runs. Drawer is opened from the
// Push to BI list card. Mirrors the R10 RunHistoryDrawer shape (list of runs,
// status pill, started/finished timestamps, error excerpt). When the real
// useStatusStream lands, in-flight runs receive live updates here.
const RunHistoryDrawer = ({ automation, onClose, onShowNotification }) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const stream = useStatusStream(automation?.id);

  const load = useCallback(async () => {
    if (!automation?.id) return;
    try {
      setLoading(true);
      const resp = await apiService.listPushRuns(automation.id, { page: 1, page_size: 50 });
      const items = resp?.items ?? (Array.isArray(resp) ? resp : []);
      setRuns(items);
    } catch (err) {
      setError(err.message || 'Failed to load run history');
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [automation]);

  useEffect(() => { load(); }, [load]);

  // When a streamed status arrives for a run that's already in the list,
  // patch it inline so the drawer reflects live progress without a full reload.
  useEffect(() => {
    if (!stream?.runId || !stream?.status) return;
    setRuns((prev) => prev.map((r) => (r.id === stream.runId ? { ...r, status: stream.status } : r)));
  }, [stream]);

  const handleCancel = async (run) => {
    try {
      await apiService.cancelPushRun(run.id);
      if (onShowNotification) onShowNotification(`Cancel requested for run ${run.id}`, 'info');
      load();
    } catch (err) {
      if (onShowNotification) onShowNotification(err.message || 'Failed to cancel run', 'error');
    }
  };

  return (
    <div className="run-history-drawer" data-testid="run-history-drawer">
      <div className="run-history-drawer-overlay" onClick={onClose} />
      <div className="run-history-drawer-panel">
        <div className="run-history-drawer-header">
          <h3>{automation?.name} — run history</h3>
          <button className="run-history-drawer-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="run-history-drawer-body">
          {loading ? (
            <p>Loading runs...</p>
          ) : error ? (
            <div className="push-step-error">{error}</div>
          ) : runs.length === 0 ? (
            <p>No runs yet.</p>
          ) : (
            <ul className="run-history-list">
              {runs.map((r) => (
                <li key={r.id} className="run-history-item">
                  <div className="run-history-item-row">
                    <Chip
                      variant={runStatusVariant(r.status)}
                      className="run-history-status"
                    >
                      {r.status || 'unknown'}
                    </Chip>
                    <span className="run-history-time">
                      {r.started_at ? <Timestamp value={r.started_at} /> : 'queued'}
                    </span>
                    {/* How long it took, rather than a second time that would read the same
                        as the first in the short form ("2h ago → 2h ago"). */}
                    {r.started_at && r.finished_at && (
                      <span className="run-history-meta">Took {formatDuration(r.started_at, r.finished_at)}</span>
                    )}
                  </div>
                  {r.rows_written != null && (
                    <div className="run-history-meta">Rows written: {r.rows_written}</div>
                  )}
                  {r.error_excerpt && (
                    <div className="run-history-error">{r.error_excerpt}</div>
                  )}
                  {(r.status === 'queued' || r.status === 'running') && (
                    <button className="push-secondary-button" onClick={() => handleCancel(r)}>Cancel</button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default RunHistoryDrawer;
